@use "../../styles/colors";

$button-box-shadow-variable-name: --ixf-button-box-shadow;
$button-box-shadow-hover-variable-name: --ixf-button-box-shadow-hover;
$button-box-shadow-focus-variable-name: --ixf-button-box-shadow-focus;
$button-box-shadow-active-variable-name: --ixf-button-box-shadow-active;
$button-box-shadow-disabled-variable-name: --ixf-button-box-shadow-disabled;

// For animation, this apparently cannot be a regular CSS variable. Browser support for @property rules is 92%+, so it's probably ok.
@property --ixf-button-rainbow-gradient-border-angle {
  syntax: "<angle>";
  initial-value: 0deg;
  inherits: false;
}

.button {
  --ixf-button-width: fit-content;

  // Variant default: primary
  --ixf-button-text-color: var(--ixf-text-tone-button);
  --ixf-button-background: var(--ixf-color-primary-md);
  --ixf-button-background-hover: var(--ixf-color-primary-md-lighten);
  --ixf-button-background-focus: var(--ixf-color-primary-md-lighten);
  --ixf-button-background-active: var(--ixf-color-primary-md-darken);
  --ixf-button-background-disabled: var(--ixf-color-primary-md);
  --ixf-button-opacity-disabled: 0.3;
  #{$button-box-shadow-variable-name}: none;
  #{$button-box-shadow-hover-variable-name}: 0 var(--ixf-space-1)
    var(--ixf-space-4) 0 rgba(53, 200, 205, 0.3);
  #{$button-box-shadow-focus-variable-name}:
    var(#{$button-box-shadow-hover-variable-name}),
    0 0 0 2px var(--ixf-text-tone-md) inset;
  #{$button-box-shadow-active-variable-name}: none;
  #{$button-box-shadow-disabled-variable-name}: none;

  // Size default: medium
  --ixf-button-border-radius: var(--ixf-border-radius-2);
  --ixf-button-display: inline-flex;
  --ixf-button-justify-content: center;
  --ixf-button-align-items: center;
  --ixf-button-gap: var(--ixf-space-1);
  --ixf-button-padding: var(--ixf-space-2) var(--ixf-space-4);
  --ixf-button-font-size: 0.875rem;
  --ixf-button-line-height: 1.5rem;
  --ixf-button-font-weight: var(--ixf-font-weight-main-medium);
  --ixf-button-font-family: var(--ixf-font-family-main);
  --ixf-button-icon-size: 1rem;
  --ixf-button-stroke-size: 0;

  position: relative;
  cursor: pointer;
  display: var(--ixf-button-display);
  justify-content: var(--ixf-button-justify-content);
  align-items: var(--ixf-button-align-items);
  gap: var(--ixf-button-gap);
  padding: var(--ixf-button-padding);
  font-size: var(--ixf-button-font-size);
  line-height: var(--ixf-button-line-height);
  font-weight: var(--ixf-button-font-weight);
  font-family: var(--ixf-button-font-family);
  border-radius: var(--ixf-button-border-radius);
  background: var(--ixf-button-background);
  color: var(--ixf-button-text-color);
  box-shadow: var(--ixf-button-box-shadow);
  width: var(--ixf-button-width);
  transition: 200ms all ease;
  white-space: pre;

  @include colors.define-best-supported-color-value(
    $button-box-shadow-hover-variable-name,
    0 4px 16px 0 color(from var(--ixf-color-primary-md) srgb r g b / 0.3),
    0 4px 16px 0 color(from var(--ixf-color-primary-md) display-p3 r g b / 0.3)
  );

  &.affix-left {
    --ixf-button-padding: var(--ixf-space-2) var(--ixf-space-4)
      var(--ixf-space-2) var(--ixf-space-3);
  }

  &.affix-right {
    --ixf-button-padding: var(--ixf-space-2) var(--ixf-space-3)
      var(--ixf-space-2) var(--ixf-space-4);
  }

  &.variant {
    &-secondary {
      --ixf-button-text-color: var(--ixf-text-tone-md);
      --ixf-button-background: var(--ixf-color-neutral-lt);
      --ixf-button-background-hover: var(--ixf-color-neutral-lt);
      --ixf-button-background-focus: var(--ixf-color-neutral-lt);
      --ixf-button-background-active: var(--ixf-color-neutral-md);
      --ixf-button-background-disabled: var(--ixf-color-neutral-md);
      #{$button-box-shadow-focus-variable-name}:
        var(#{$button-box-shadow-hover-variable-name}),
        0 0 0 2px var(--ixf-text-tone-md) inset;

      @include colors.define-best-supported-color-value(
        $button-box-shadow-hover-variable-name,
        0 4px 16px 0 color(from var(--ixf-text-tone-md) srgb r g b / 0.07),
        0 4px 16px 0 color(from var(--ixf-text-tone-md) display-p3 r g b / 0.07)
      );
    }

    &-danger {
      --ixf-button-background: var(--ixf-color-danger-md);
      --ixf-button-background-hover: var(--ixf-color-danger-md);
      --ixf-button-background-focus: var(--ixf-color-danger-md);
      --ixf-button-background-active: var(--ixf-color-danger-md-darken);
      --ixf-button-background-disabled: var(--ixf-color-danger-md);
      #{$button-box-shadow-focus-variable-name}:
        var(#{$button-box-shadow-hover-variable-name}),
        0 0 0 2px var(--ixf-text-tone-md) inset;

      @include colors.define-best-supported-color-value(
        $button-box-shadow-hover-variable-name,
        0 4px 16px 0 color(from var(--ixf-color-danger-md) srgb r g b / 0.4),
        0 4px 16px 0
          color(from var(--ixf-color-danger-md) display-p3 r g b / 0.4)
      );
    }

    &-light {
      --ixf-button-text-color: var(--ixf-text-tone-md);
      --ixf-button-background: none;
      --ixf-button-background-hover: none;
      --ixf-button-background-focus: none;
      --ixf-button-background-active: var(--ixf-color-neutral-xlt);
      --ixf-button-background-disabled: none;
      --ixf-button-opacity-disabled: 0.3;
      #{$button-box-shadow-variable-name}: 0 0 0 1px var(--ixf-color-separator)
        inset;
      #{$button-box-shadow-active-variable-name}: var(
        #{$button-box-shadow-variable-name}
      );
      #{$button-box-shadow-disabled-variable-name}: var(
        #{$button-box-shadow-variable-name}
      );

      @include colors.define-best-supported-color-value(
        $button-box-shadow-hover-variable-name,
        #{var(#{$button-box-shadow-variable-name}),
        0 4px 16px 0 color(from var(--ixf-text-tone-md) srgb r g b / 0.07)},
        #{var(#{$button-box-shadow-variable-name}),
        0 4px 16px 0
          color(from var(--ixf-text-tone-md) display-p3 r g b / 0.07)}
      );

      @include colors.define-best-supported-color-value(
        $button-box-shadow-focus-variable-name,
        #{0 0 0 2px var(--ixf-text-tone-md) inset,
        0 4px 16px 0 color(from var(--ixf-text-tone-md) srgb r g b / 0.07)},
        #{0 0 0 2px var(--ixf-text-tone-md) inset,
        0 4px 16px 0
          color(from var(--ixf-text-tone-md) display-p3 r g b / 0.07)}
      );
    }

    &-light-reverse {
      --ixf-button-text-color: var(--ixf-color-background);
      --ixf-button-background: none;
      --ixf-button-background-hover: none;
      --ixf-button-background-focus: none;
      --ixf-button-background-disabled: none;
      --ixf-button-opacity-disabled: 0.3;
      #{$button-box-shadow-variable-name}: 0 0 0 1px var(--ixf-color-neutral-dk)
        inset;
      #{$button-box-shadow-active-variable-name}: var(
        #{$button-box-shadow-variable-name}
      );
      #{$button-box-shadow-disabled-variable-name}: var(
        #{$button-box-shadow-variable-name}
      );

      @include colors.define-best-supported-color-value(
        $button-box-shadow-hover-variable-name,
        #{var(#{$button-box-shadow-variable-name}),
        0 4px 16px 0 color(from var(--ixf-text-tone-md) srgb r g b / 0.07)},
        #{var(#{$button-box-shadow-variable-name}),
        0 4px 16px 0
          color(from var(--ixf-text-tone-md) display-p3 r g b / 0.07)}
      );

      @include colors.define-best-supported-color-value(
        $button-box-shadow-focus-variable-name,
        #{0 0 0 2px var(--ixf-color-background) inset,
        0 4px 16px 0 color(from var(--ixf-text-tone-md) srgb r g b / 0.07)},
        #{0 0 0 2px var(--ixf-color-background) inset,
        0 4px 16px 0
          color(from var(--ixf-text-tone-md) display-p3 r g b / 0.07)}
      );

      @include colors.define-best-supported-color-value(
        #{--ixf-button-background-active},
        color(from var(--ixf-color-background) srgb r g b / 0.05),
        color(from var(--ixf-color-background) display-p3 r g b / 0.05)
      );
    }

    &-fancy {
      --ixf-button-text-color: var(--ixf-text-tone-md);
      --ixf-button-background: none;
      --ixf-button-background-hover: var(--ixf-color-neutral-xlt);
      --ixf-button-background-focus: none;
      --ixf-button-background-active: var(--ixf-color-neutral-xlt);
      --ixf-button-background-disabled: none;
      --ixf-button-opacity-disabled: 0.3;
      #{$button-box-shadow-variable-name}: var(--ixf-box-shadow-fancy-radio);
      #{$button-box-shadow-active-variable-name}: var(
        --ixf-box-shadow-fancy-radio-active
      );
      #{$button-box-shadow-disabled-variable-name}: var(
        --ixf-box-shadow-fancy-radio
      );
      #{$button-box-shadow-hover-variable-name}: var(
        --ixf-box-shadow-fancy-radio
      );
      #{$button-box-shadow-focus-variable-name}: var(
        --ixf-box-shadow-fancy-radio-focus
      );
    }

    &-rainbow {
      @keyframes rainbow-spin {
        to {
          --ixf-button-rainbow-gradient-border-angle: 360deg;
        }
      }

      --ixf-button-stroke-size: 1px;
      --ixf-button-text-color: var(--ixf-text-tone-md);
      --ixf-button-background: transparent;
      --ixf-button-background-hover: none;
      --ixf-button-background-focus: none;
      --ixf-button-background-active: none;
      --ixf-button-background-disabled: none;
      --ixf-button-opacity-disabled: 0.3;
      #{$button-box-shadow-variable-name}: none;
      #{$button-box-shadow-active-variable-name}: var(
        #{$button-box-shadow-variable-name}
      );
      #{$button-box-shadow-disabled-variable-name}: var(
        #{$button-box-shadow-variable-name}
      );
      #{$button-box-shadow-focus-variable-name}: var(
        #{$button-box-shadow-variable-name}
      );
      #{$button-box-shadow-hover-variable-name}: var(
        #{$button-box-shadow-variable-name}
      );

      isolation: isolate;

      &:hover,
      &:focus-visible,
      &.appearance-hovered,
      &.appearance-focused,
      &:active {
        --ixf-button-stroke-size: 2px;

        &::before {
          background: conic-gradient(
            from var(--ixf-button-rainbow-gradient-border-angle),
            var(--ixf-color-primary-md),
            var(--ixf-color-tertiary-md),
            var(--ixf-color-secondary-md),
            var(--ixf-color-quaternary-md),
            var(--ixf-color-danger-md),
            var(--ixf-color-primary-md)
          );
          animation: rainbow-spin 3s linear infinite;
        }

        &::after {
          opacity: 0.05;
        }
      }

      &::before {
        content: "";
        position: absolute;
        inset: 0;
        border-radius: inherit;
        padding: var(--ixf-button-stroke-size);
        background: var(--ixf-color-separator);
        /* Two mask layers: full box minus the inner content box = only the ring */
        mask:
          linear-gradient(#000 0 0) content-box,
          linear-gradient(#000 0 0);
        -webkit-mask:
          linear-gradient(#000 0 0) content-box,
          linear-gradient(#000 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;
        pointer-events: none;
        transition: padding 200ms ease;
      }

      &::after {
        content: "";
        position: absolute;
        inset: 0;
        z-index: -1;
        border-radius: inherit;
        background: var(--ixf-color-rainbow-gradient);
        opacity: 0;
        transition: opacity 200ms ease;
        pointer-events: none;
      }

      @media (prefers-reduced-motion: reduce) {
        &::before {
          animation: none;
        }
      }
    }
  }

  &.size {
    &-xsmall {
      --ixf-button-font-size: 0.75rem;
      --ixf-button-line-height: 1rem;
      --ixf-button-padding: var(--ixf-space-1) var(--ixf-space-2);
      --ixf-button-gap: var(--ixf-space-1);
      --ixf-button-border-radius: var(--ixf-border-radius-1);

      &.affix-left {
        --ixf-button-padding: var(--ixf-space-1) var(--ixf-space-2)
          var(--ixf-space-1) var(--ixf-space-1);
      }

      &.affix-right {
        --ixf-button-padding: var(--ixf-space-1) var(--ixf-space-1)
          var(--ixf-space-1) var(--ixf-space-2);
      }
    }

    &-small {
      --ixf-button-padding: var(--ixf-space-1) var(--ixf-space-3);
      --ixf-button-gap: var(--ixf-space-1);
      --ixf-button-border-radius: 6px;

      &.affix-left {
        --ixf-button-padding: var(--ixf-space-1) var(--ixf-space-3)
          var(--ixf-space-1) var(--ixf-space-2);
      }

      &.affix-right {
        --ixf-button-padding: var(--ixf-space-1) var(--ixf-space-2)
          var(--ixf-space-1) var(--ixf-space-3);
      }
    }

    &-large {
      --ixf-button-padding: var(--ixf-space-3) var(--ixf-space-6);
      --ixf-button-font-size: 1rem;
      --ixf-button-line-height: 1.5rem;
      --ixf-button-gap: var(--ixf-space-2);

      &.affix-left {
        --ixf-button-padding: var(--ixf-space-3) var(--ixf-space-6)
          var(--ixf-space-3) var(--ixf-space-5);
      }

      &.affix-right {
        --ixf-button-padding: var(--ixf-space-3) var(--ixf-space-5)
          var(--ixf-space-3) var(--ixf-space-6);
      }
    }
  }

  &.iconOnly {
    &.size-xsmall {
      --ixf-button-padding: var(--ixf-space-1);
    }

    &.size-small {
      --ixf-button-padding: var(--ixf-space-2);
    }

    &.size-medium {
      --ixf-button-padding: var(--ixf-space-3);
    }

    &.size-large {
      --ixf-button-padding: var(--ixf-space-4);
    }
  }

  &.fullWidth {
    --ixf-button-width: 100%;
  }

  svg path {
    fill: var(--ixf-button-text-color);
  }

  &:focus-visible,
  &.appearance-focused {
    outline: none;
    background: var(--ixf-button-background-focus);
    box-shadow: var(#{$button-box-shadow-focus-variable-name});
  }

  &:hover,
  &.appearance-hovered {
    background: var(--ixf-button-background-hover);
    box-shadow: var(#{$button-box-shadow-hover-variable-name});
    outline: none;
  }

  &:active,
  &.appearance-active {
    background: var(--ixf-button-background-active);
    box-shadow: var(#{$button-box-shadow-active-variable-name});
    outline: none;
  }

  &:disabled,
  &.appearance-disabled {
    opacity: var(--ixf-button-opacity-disabled);
    background: var(--ixf-button-background-disabled);
    box-shadow: var(#{$button-box-shadow-disabled-variable-name});
    outline: none;
    cursor: not-allowed;
  }

  &.appearance-enabled {
    background: var(--ixf-button-background-active);
    box-shadow: 0 0 0 2px var(--ixf-text-tone-md) inset;
    outline: none;
  }

  &.appearance-selected-radio {
    background: var(--ixf-button-background-active);
    box-shadow: var(#{$button-box-shadow-focus-variable-name});
    outline: none;
  }

  &.appearance-plain {
    background: none;
    box-shadow: none;
    outline: none;
    border: none;
  }

  &.state-loading,
  &.state-error,
  &.state-success {
    opacity: 0.3;
  }

  &.state-error {
    background: var(--ixf-color-danger-md);
  }

  @keyframes state-in {
    from {
      transform: scale(3);
    }
    to {
      transform: scale(1);
    }
  }

  .loader {
    height: 1rem;
    width: 1rem;
    position: absolute;
    display: inline-flex;
    justify-content: center;
    align-items: center;
  }

  .stateIndicatorContainer {
    animation: state-in 150ms ease-in;
    height: var(--ixf-button-icon-size);
    width: var(--ixf-button-icon-size);
    position: absolute;
    display: inline-flex;
    justify-content: center;
    align-items: center;
  }

  .content {
    display: var(--ixf-button-display);
    justify-content: var(--ixf-button-justify-content);
    align-items: var(--ixf-button-align-items);
    gap: var(--ixf-button-gap);
    color: var(--ixf-button-text-color);

    &.isPlaceholder {
      visibility: hidden;
    }

    .affixIcon,
    .affixIcon > svg {
      height: var(--ixf-button-icon-size);
      width: var(--ixf-button-icon-size);
      display: inline-flex;
      justify-content: center;
      align-items: center;
      flex: 0 0 auto;
      fill: var(--ixf-button-text-color);
    }
  }
}
